import React, {useRef} from 'react';
import {GatsbyImage} from 'gatsby-plugin-image';
import type {ImageType} from '../../types/block';

type Props = {
  className: string;
  objectFit?: 'contain' | 'cover';
  objectPosition?: 'left center' | 'center center' | 'bottom center' | 'center right' | 'center top';
  loading?: 'lazy' | 'eager';
  block: {
    image: ImageType;
    __typename?: string;
  };
};

export const Image = (props: Props) => {
  const images = props.block.image.gatsbyImageData;
  const imgSiblingRef = useRef<HTMLDivElement | null>(null);

  // Eager loading causes artefacts https://github.com/gatsbyjs/gatsby/issues/31031, this hack is until we have an official fix
  const fixPlaceholderOpacity = () => {
    if (props.loading !== 'eager' || !imgSiblingRef.current) {
      return;
    }

    // GatsbyImage doesn't expose ref, that's why we put invisible element next to it
    // so that we could query only the placeholder related to the current image
    const placeholder: HTMLImageElement = imgSiblingRef.current.parentElement.querySelector('[data-placeholder-image]');

    if (!placeholder) {
      return;
    }

    // GatsbyImage doesn't hide placeholder in eager mode once image is cached
    // so we force hide it here
    placeholder.style.opacity = '0';
  };

  return (
    <>
      <div className='hidden' ref={imgSiblingRef} />
      {props.block.image.url.endsWith('.svg') ? (
        <img
          loading='lazy'
          data-testid='simple-img'
          className={props.className}
          src={props.block.image.url}
          alt={props.block.image.alt || props.block.image.filename}
          title={props.block.image.title || props.block.image.filename}
        />
      ) : (
        <GatsbyImage
          className={props.className}
          objectFit={props.objectFit}
          objectPosition={props.objectPosition || 'center center'}
          alt={props.block.image.alt || props.block.image.filename}
          title={props.block.image.title || props.block.image.filename}
          image={images}
          onLoad={fixPlaceholderOpacity}
          loading={props.loading}
        />
      )}
    </>
  );
};
